import React, { ReactNode, useMemo } from 'react';
import { parse as parseDate } from 'date-fns';
import { Link } from 'react-router-dom';

import { IconStatementHistory, DateSeries } from '@trqk/components';
import {
    locale,
    sortByEarliestDate,
    getSortableStatementPeriodLabel,
} from '@trqk/utils';

import { HASHES, PATHS } from '../../../../../../constants';
import { DataCard } from '../components';
// import data from './dummyData.json';

interface StatementHistoryCardProps {
    data: InsightStatementHistoryData[];
    entityName: ReactNode;
}

export const StatementHistoryCard = ({
    data,
    entityName,
}: StatementHistoryCardProps): JSX.Element => {
    const dataWithDates = useMemo(() => {
        const sortedData = [...data].sort(
            (
                a: InsightStatementHistoryData,
                b: InsightStatementHistoryData,
            ): number => {
                const periodEndA = a.period?.end_date ?? null;
                const periodEndB = b.period?.end_date ?? null;

                return sortByEarliestDate(periodEndA, periodEndB);
            },
        );

        const mappedData = sortedData.reduce(
            (
                acc: InsightStatementHistoryDateData[],
                item: InsightStatementHistoryData,
            ): InsightStatementHistoryDateData[] => {
                const endDate = item?.period?.end_date;
                const startDate = item?.period?.start_date;
                const earnings = item?.earnings ?? null;

                if (earnings !== null && endDate && startDate) {
                    const date = parseDate(endDate, 'yyyy-MM-dd', new Date());

                    acc.push({
                        ...item,
                        date,
                        displayDate: getSortableStatementPeriodLabel(
                            item?.period ?? null,
                            '',
                        ),
                    });
                }

                return acc;
            },
            [] as InsightStatementHistoryDateData[],
        );

        return mappedData;
    }, [data]);

    return (
        <DataCard
            title={locale.enUS.INSIGHTS.CARDS.STATEMENT_HISTORY.TITLE}
            color="primary"
            header={
                <>
                    {locale.enUS.INSIGHTS.CARDS.STATEMENT_HISTORY.HEADER.PART_1}
                    <br />
                    {entityName}
                </>
            }
            Icon={IconStatementHistory}
            inInsightsIndex={false}
        >
            <DateSeries<InsightStatementHistoryDateData>
                bulletThreshold={50}
                data={dataWithDates}
                dateDataFields={{
                    value: 'earnings',
                    label: 'Earnings',
                }}
                height={180}
                numberFormat="$#.00a"
                tooltipText="[bold]{displayDate}[/]: {earnings}"
            />
            <p className="w-full text-right pr-6">
                <Link
                    className="text-sm underline text-primary-500 leading-loose"
                    to={({ search }) => ({
                        pathname: PATHS.REPORTS,
                        hash: HASHES.REPORTS.TOTAL_ROYALTIES,
                        search,
                    })}
                >
                    {
                        locale.enUS.INSIGHTS.CARDS.STATEMENT_HISTORY
                            .VIEW_FULL_REPORT
                    }
                </Link>
            </p>
        </DataCard>
    );
};
